import { useAnalyticsFilterContext } from "@hooks/context/useAnalyticsFilterContext";
import { getApiData } from "@hooks/utils/api";
import { catchSentryError } from "@utils/sentry";
import { PeriodPreset, resolvePeriod } from "@utils/reportingPeriod";
import { DashboardScope } from "catchment-common/interfaces/dashboardSurface";
import { useCallback, useEffect, useState } from "react";

export interface OrgDataOptions {
  /** Per-endpoint query params (e.g. the benchmark `metric`). */
  extra?: { [key: string]: string };
  /** Always request every accessible project, whatever the header's Project
   * Scope says. The by-project comparison and benchmark endpoints set this:
   * they exist to rank projects against one another, so narrowing them to the
   * selected project would leave a comparison of one thing. Their UI highlights
   * the selection instead. */
  ignoreProjectFilter?: boolean;
  /**
   * Skip the request entirely and stay empty. For a slice whose parameters are
   * not decided yet — the breakdown trend before its tab has settled on a
   * bucket. Asking for "no buckets" would render an empty chart that looks like
   * real data saying zero, and the project trend hook skips for the same reason.
   */
  skip?: boolean;
  /** Always request all time, whatever Reporting Period the header shows. The
   * two dimensions are separately switchable: the by-project comparisons ignore
   * the project filter but still honour the period, while the org home page and
   * the selector's own option list ignore the period. */
  ignorePeriodFilter?: boolean;
}

/**
 * Shared fetch hook for every org-Insights endpoint (callFn
 * `organisationDashboard`). The backend returns the exact camelCase model, so
 * there's no decode step.
 *
 * The header's Project Scope and Reporting Period are read from
 * {@link useAnalyticsFilterContext} here rather than threaded through the ~30
 * call sites that would otherwise each have to pass them. The period is
 * resolved to month bounds before it goes on the wire — the backend never sees
 * a preset. Params are serialised for stable effect dependencies.
 */
export const useOrgData = <T,>(path: string, options?: OrgDataOptions) => {
  const { periodBySurface, projectIds } = useAnalyticsFilterContext();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [data, setData] = useState<T>();

  const range = resolvePeriod(
    options?.ignorePeriodFilter
      ? PeriodPreset.AllTime
      : periodBySurface[DashboardScope.Organisation]
  );
  // The range rides in the query params the backend already parses; the
  // all-time sentinels keep every request shape identical, chosen period or not.
  // It is spread last deliberately: the period is the shell's to set, and no
  // endpoint carries dates of its own.
  const serializedExtra = JSON.stringify({ ...options?.extra, ...range });
  const serializedIds = JSON.stringify(
    options?.ignoreProjectFilter ? [] : projectIds
  );

  const skip = options?.skip ?? false;

  const fetchData = useCallback(async () => {
    // A skipped request still has to CLEAR the loading flag: it starts `true` so
    // a card shows a skeleton rather than an empty chart on mount, and a flag
    // only cleared inside the fetch would leave a permanently grey card for a
    // tab that never settles on a bucket.
    if (skip) {
      // The previous answer is dropped too. A slice that stops being asked for
      // has no current answer, and leaving the last one on screen — with no
      // skeleton, since the flag just cleared — would show one bucket's series
      // under a selection that no longer includes it.
      setData(undefined);
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      const result = await getApiData(
        path,
        "organisationDashboard",
        serializedIds,
        JSON.parse(serializedExtra)
      );
      if (result.data != null) {
        setData(result.data as unknown as T);
      }
    } catch (error) {
      catchSentryError(error);
    } finally {
      setIsLoading(false);
    }
  }, [path, serializedExtra, serializedIds, skip]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, isLoading };
};
